import { useState } from 'react';
import { Button } from '@/components/ui/button';
import { Dialog, DialogContent, DialogHeader, DialogTitle } from '@/components/ui/dialog';
import { HelpCircle, ChevronRight, BookOpen } from 'lucide-react';

export const HelpCenter = () => {
  const [isOpen, setIsOpen] = useState(false);
  const [selectedSection, setSelectedSection] = useState(null);

  const helpSections = {
    gettingStarted: {
      title: '🚀 Getting Started',
      icon: '🚀',
      steps: [
        {
          title: 'What is JTA AI Data Labeling?',
          content: 'It\'s a tool that helps you add labels (tags/categories) to your text data automatically using AI. Think of it like having a smart assistant that reads your text and organizes it for you.'
        },
        {
          title: 'What can I do with it?',
          content: 'You can:\n• Categorize customer reviews (positive/negative)\n• Extract information from medical notes\n• Detect personal information in documents\n• Analyze sentiment in feedback\n• Much more - all automatically!'
        },
        {
          title: 'Do I need technical knowledge?',
          content: 'No! This tool is designed for everyone. Just follow the simple steps and the AI does the hard work.'
        }
      ]
    },
    createProject: {
      title: '📁 Creating Your First Project',
      icon: '📁',
      steps: [
        {
          title: 'Step 1: Click "New Project" Button',
          content: 'On the homepage, you\'ll see a purple button that says "New Project". Click it.'
        },
        {
          title: 'Step 2: Give Your Project a Name',
          content: 'Type a simple name like:\n• "Customer Reviews"\n• "Medical Notes"\n• "Patient Feedback"\n\nThis helps you remember what data you\'re working with.'
        },
        {
          title: 'Step 3: Add a Description (Optional)',
          content: 'Write a short note about what this project is for. You can skip this if you want.'
        },
        {
          title: 'Step 4: Click "Create Project"',
          content: 'Click the button and your project is ready! You\'ll see it appear as a card on your dashboard.'
        }
      ]
    },
    uploadData: {
      title: '📤 Uploading Your Data',
      icon: '📤',
      steps: [
        {
          title: 'Step 1: Click on Your Project',
          content: 'Find your project card on the dashboard and click it. This opens the workspace.'
        },
        {
          title: 'Step 2: Find the Upload Zone',
          content: 'On the left side, you\'ll see a box with an upload icon that says "Click to upload".'
        },
        {
          title: 'Step 3: Prepare Your File',
          content: 'Your data should be in one of these formats:\n\n**TXT File:**\nEach line = one item to label\nExample:\nThis is great!\nThis is terrible\nThis is okay\n\n**CSV File:**\nEach row = one item\nExample:\nid,text\n1,"This is great!"\n2,"This is terrible"'
        },
        {
          title: 'Step 4: Click Upload Zone',
          content: 'Click the upload box, select your file, and wait. You\'ll see a success message when it\'s done.'
        },
        {
          title: 'Step 5: Check Your Items',
          content: 'Look at the right side - all your text items are now listed there!'
        }
      ]
    },
    choosingMode: {
      title: '🎯 Choosing the Right Labeling Mode',
      icon: '🎯',
      steps: [
        {
          title: 'What are Labeling Modes?',
          content: 'Different ways the AI can analyze your text. Each mode does something different.'
        },
        {
          title: 'Common Modes Explained Simply:',
          content: '**Classification** - Put items into categories\nExample: "Good", "Bad", "Neutral"\n\n**Sentiment Analysis** - Is it positive or negative?\nExample: "Positive", "Negative", "Neutral"\n\n**Named Entity Recognition (NER)** - Find names, places, dates, etc.\nExample: "Person: John, Location: New York"\n\n**PII Detection** - Find personal information\nExample: "Email: john@email.com, Phone: 555-1234"\n\n**Emotion Detection** - What emotion is expressed?\nExample: "Joy", "Anger", "Sadness"'
        },
        {
          title: 'How to Choose?',
          content: '**If you have customer reviews:** Use Sentiment Analysis\n**If you have medical notes:** Use Named Entity Recognition\n**If you need to find personal info:** Use PII Detection\n**If you want custom categories:** Use Classification\n\nStill confused? Click the AI chat button and ask!'
        }
      ]
    },
    usingAI: {
      title: '⚡ Using AI Auto-Labeling',
      icon: '⚡',
      steps: [
        {
          title: 'Step 1: Open Your Project Workspace',
          content: 'Click on your project from the dashboard to enter the workspace.'
        },
        {
          title: 'Step 2: Find the "AI Auto-Label" Section',
          content: 'On the left side, scroll down to find the purple box that says "AI Auto-Label".'
        },
        {
          title: 'Step 3: Select a Labeling Mode',
          content: 'Click the dropdown menu and choose what you want the AI to do:\n• Classification (for categories)\n• Sentiment (for positive/negative)\n• NER (to find names, places, etc.)\n• And more!'
        },
        {
          title: 'Step 4: Add Instructions (Optional)',
          content: 'Want specific categories? Type them in the "Custom Instructions" box.\n\nExample:\n"Categorize as: Excellent, Good, Average, Poor"\n\nOr leave it blank to let AI decide!'
        },
        {
          title: 'Step 5: Click "Label All"',
          content: 'Click the purple "Label All" button and watch the magic happen! The AI will label all your items in seconds.'
        },
        {
          title: 'Step 6: Check the Results',
          content: 'Look at the right panel - all items now have labels with a purple "AI" badge!'
        }
      ]
    },
    reviewing: {
      title: '✏️ Reviewing & Editing Labels',
      icon: '✏️',
      steps: [
        {
          title: 'Why Review?',
          content: 'AI is smart but not perfect. You should check the labels to make sure they\'re correct.'
        },
        {
          title: 'How to Review:',
          content: '1. Look at each item in the right panel\n2. Read the text\n3. Check if the AI label makes sense\n4. If wrong, click "Edit" button next to the label'
        },
        {
          title: 'How to Edit a Label:',
          content: '1. Click the "Edit" button next to any label\n2. A text box appears\n3. Type the correct label\n4. Press Enter\n5. Done! You\'ll see a green checkmark showing it\'s reviewed'
        },
        {
          title: 'What do the Badges Mean?',
          content: '**Purple "AI" Badge** = AI generated this label\n**Green Checkmark** = You reviewed and approved this\n**No Badge** = Not labeled yet'
        }
      ]
    },
    exporting: {
      title: '💾 Exporting Your Labeled Data',
      icon: '💾',
      steps: [
        {
          title: 'Step 1: Finish Labeling',
          content: 'Make sure all your items are labeled and reviewed.'
        },
        {
          title: 'Step 2: Find Export Buttons',
          content: 'At the top right of the workspace, you\'ll see two buttons:\n• "JSON" button\n• "CSV" button'
        },
        {
          title: 'Step 3: Choose Your Format',
          content: '**Click JSON if:**\n• You\'re training a machine learning model\n• You need data for programming\n\n**Click CSV if:**\n• You want to open in Excel\n• You want to analyze in Google Sheets\n• You want simple spreadsheet format'
        },
        {
          title: 'Step 4: Download',
          content: 'Click the button and your file downloads automatically. Find it in your Downloads folder!'
        },
        {
          title: 'What\'s in the File?',
          content: 'Your file contains:\n• Original text\n• AI-generated label\n• Whether it was reviewed\n• Confidence score\n\nEverything you need!'
        }
      ]
    },
    healthcare: {
      title: '🏥 Healthcare Features',
      icon: '🏥',
      steps: [
        {
          title: 'Clinical Notes Analysis',
          content: 'Extract medical information from doctor\'s notes:\n\n1. Create a project\n2. Upload notes (one per line)\n3. Select "Named Entity Recognition"\n4. Add instruction: "Extract diagnoses, medications, procedures"\n5. Click Label All\n\nResult: Gets all medical terms automatically!'
        },
        {
          title: 'PII/PHI Detection for HIPAA',
          content: 'Find personal health information:\n\n1. Create a project\n2. Upload medical records\n3. Select "PII Detection" mode\n4. Click Label All\n\nResult: Finds patient names, dates, medical record numbers, etc.'
        },
        {
          title: 'Patient Feedback Analysis',
          content: 'Understand what patients are saying:\n\n1. Create a project\n2. Upload patient reviews\n3. Select "Sentiment Analysis"\n4. Click Label All\n\nResult: Shows which feedback is positive, negative, or neutral!'
        },
        {
          title: 'HIPAA Compliance',
          content: 'This tool helps with HIPAA compliance by:\n• Detecting all personal health information\n• Identifying what needs to be removed\n• Preparing data for de-identification\n\nNote: Always consult with your compliance officer for final approval.'
        }
      ]
    },
    troubleshooting: {
      title: '🔧 Common Issues & Solutions',
      icon: '🔧',
      steps: [
        {
          title: 'My file won\'t upload',
          content: 'Check:\n• Is it a .txt or .csv file?\n• Is the file too big? (Keep under 5MB)\n• Is each line/row formatted correctly?\n• Try saving the file again and re-upload'
        },
        {
          title: 'AI labels don\'t make sense',
          content: 'Try:\n• Add custom instructions to guide the AI\n• Choose a different labeling mode\n• Check if your text is clear and readable\n• Edit the wrong labels manually'
        },
        {
          title: 'I can\'t find my project',
          content: 'Solution:\n• Scroll down on the dashboard\n• Projects are shown as cards\n• Check if you created the project successfully'
        },
        {
          title: 'Export button doesn\'t work',
          content: 'Make sure:\n• At least one item is labeled\n• You\'re in the workspace (not dashboard)\n• Your browser allows downloads'
        },
        {
          title: 'Need more help?',
          content: 'Click the chat button (bottom right) and ask questions! The AI assistant is there to help you 24/7.'
        }
      ]
    },
    chatbot: {
      title: '💬 Using the AI Assistant',
      icon: '💬',
      steps: [
        {
          title: 'Where is the AI Assistant?',
          content: 'Look at the bottom right corner of your screen. You\'ll see a round purple button with a chat icon.'
        },
        {
          title: 'How to Use It:',
          content: '1. Click the chat button\n2. Type your question in plain English\n3. Press Enter or click Send\n4. Read the answer\n5. Ask follow-up questions if needed'
        },
        {
          title: 'What Can I Ask?',
          content: 'Anything! Examples:\n• "How do I label medical notes?"\n• "What mode should I use for reviews?"\n• "How do I detect personal information?"\n• "I\'m stuck, help!"\n\nThe assistant knows everything about this tool!'
        },
        {
          title: 'Quick Action Buttons',
          content: 'When you first open chat, you\'ll see 4 quick buttons with common questions. Click any to get instant answers!'
        }
      ]
    }
  };

  return (
    <>
      {/* Help Button */}
      <Button
        onClick={() => setIsOpen(true)}
        className="fixed top-6 right-6 z-40 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white shadow-lg rounded-full h-12 px-6 flex items-center gap-2"
        data-testid="help-center-btn"
      >
        <HelpCircle className="w-5 h-5" />
        <span className="font-semibold">Help Center</span>
      </Button>

      {/* Help Center Modal */}
      <Dialog open={isOpen} onOpenChange={setIsOpen}>
        <DialogContent className="bg-white border-0 shadow-2xl sm:rounded-2xl max-w-5xl mx-4 max-h-[90vh] overflow-hidden">
          <DialogHeader className="border-b pb-4">
            <DialogTitle className="text-2xl sm:text-3xl flex items-center gap-3">
              <BookOpen className="w-8 h-8 text-blue-600" />
              Complete User Guide - Step by Step
            </DialogTitle>
            <p className="text-sm text-gray-600 mt-2">
              Everything you need to know to use JTA AI Data Labeling like a pro!
            </p>
          </DialogHeader>

          <div className="grid grid-cols-12 gap-0 overflow-hidden" style={{height: 'calc(90vh - 120px)'}}>
            {/* Sidebar - Sections List */}
            <div className="col-span-4 border-r overflow-y-auto bg-gray-50 p-4">
              <h3 className="text-xs font-bold text-gray-500 uppercase mb-3 px-2">Topics</h3>
              <div className="space-y-2">
                {Object.entries(helpSections).map(([key, section]) => (
                  <button
                    key={key}
                    onClick={() => setSelectedSection(key)}
                    className={`w-full text-left px-4 py-3 rounded-xl transition-all flex items-center justify-between ${
                      selectedSection === key
                        ? 'bg-gradient-to-r from-blue-500 to-blue-600 text-white shadow-md'
                        : 'bg-white hover:bg-gray-100 text-gray-700'
                    }`}
                  >
                    <div className="flex items-center gap-3">
                      <span className="text-xl">{section.icon}</span>
                      <span className="text-sm font-medium">{section.title.substring(2)}</span>
                    </div>
                    <ChevronRight className={`w-4 h-4 ${selectedSection === key ? 'text-white' : 'text-gray-400'}`} />
                  </button>
                ))}
              </div>
            </div>

            {/* Content Area */}
            <div className="col-span-8 overflow-y-auto p-6">
              {!selectedSection ? (
                <div className="flex flex-col items-center justify-center h-full text-center">
                  <div className="w-20 h-20 rounded-full bg-blue-100 flex items-center justify-center mb-4">
                    <HelpCircle className="w-10 h-10 text-blue-600" />
                  </div>
                  <h3 className="text-2xl font-bold text-gray-900 mb-2">Welcome to Help Center!</h3>
                  <p className="text-gray-600 max-w-md">
                    Select any topic from the left to see detailed step-by-step instructions.
                  </p>
                  <div className="mt-8 text-left bg-blue-50 rounded-xl p-6 max-w-lg">
                    <p className="font-semibold text-blue-900 mb-3">👋 New here? Start with:</p>
                    <ul className="space-y-2 text-sm text-blue-800">
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                        <span>🚀 Getting Started</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                        <span>📁 Creating Your First Project</span>
                      </li>
                      <li className="flex items-center gap-2">
                        <div className="w-1.5 h-1.5 rounded-full bg-blue-600"></div>
                        <span>📤 Uploading Your Data</span>
                      </li>
                    </ul>
                  </div>
                </div>
              ) : (
                <div>
                  <h2 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-3">
                    <span className="text-3xl">{helpSections[selectedSection].icon}</span>
                    {helpSections[selectedSection].title.substring(2)}
                  </h2>
                  
                  <div className="space-y-6">
                    {helpSections[selectedSection].steps.map((step, index) => (
                      <div key={index} className="bg-gray-50 rounded-xl p-5 border-l-4 border-blue-500">
                        <h4 className="font-bold text-gray-900 mb-3 flex items-center gap-2">
                          <div className="w-7 h-7 rounded-full bg-blue-500 text-white flex items-center justify-center text-sm font-bold">
                            {index + 1}
                          </div>
                          {step.title}
                        </h4>
                        <p className="text-sm text-gray-700 leading-relaxed whitespace-pre-line ml-9">
                          {step.content}
                        </p>
                      </div>
                    ))}
                  </div>

                  {selectedSection === 'troubleshooting' && (
                    <div className="mt-8 bg-gradient-to-r from-blue-50 to-slate-50 rounded-xl p-6 border-2 border-blue-200">
                      <p className="font-semibold text-blue-900 mb-2">💬 Still Need Help?</p>
                      <p className="text-sm text-blue-800">
                        Don't worry! Click the chat button at the bottom right of your screen. 
                        Our AI assistant is available 24/7 to answer any questions you have.
                      </p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HelpCenter;
